<template>
  <div>
    <confirm-reverse-transaction-modal
      :id="transactionId"
      @reversed="$emit('reversed')"
    />
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur="0"
      opacity="1"
      rounded="sm"
      :show="loadingTransactions"
    >
      <transaction-form
        :transaction-type="transactionType"
        :is-side-bar-active.sync="displaySideBar"
        @refetch-driver-wallet="$emit('refetch-driver-wallet')"
        @refetch-driver-inactivations="$emit('refetch-driver-inactivations')"
      />
      <b-card>
        <b-row class="mb-1">
          <b-col md="12">
            <h2 class="mb-1">
              Transaction History
            </h2>
          </b-col>
          <b-col
            md="12"
            class="d-flex align-items-center"
          >
            <v-select
              v-model="accountNameFilter"
              style="flex:1"
              class="mx-1"
              :options="types"
              option-label="label"
              :reduce="val => val.id"
              placeholder="Search by account name"
            />
            <b-button
              variant="outline-primary"
              class="mr-1"
              @click="refetchPaginated"
            >
              Search
            </b-button>
            <b-button
              v-if="$can('write', 'driver-wallet-transactions')"
              class="btn-icon rounded"
              variant="success"
              @click="selectTransaction('add')"
            >
              Add Transaction
            </b-button>
            <b-button
              v-if="$can('write', 'driver-wallet-transactions')"
              class="btn-icon rounded ml-2 mr-2"
              variant="primary"
              @click="selectTransaction('deduct')"
            >
              Deduct Transaction
            </b-button>
            <feather-icon
              style="cursor:pointer"
              size="20"
              icon="DownloadIcon"
              @click="downloadTransactionList"
            />
          </b-col>
        </b-row>
        <b-table
          ref="refDriverTransactionListTable"
          class="position-relative"
          :items="transactions"
          responsives
          :fields="tableColumns"
          primary-key="id"
          show-empty
          empty-text="No matching records found"
        >
          <template #cell(actions)="data">
            <b-button
              v-if="displayReverseBtn(data.item)"
              variant="primary"
              :disabled="!$can('write', 'transaction-actions')"
              @click="reverseTransaction(data.item.id)"
            >
              Reverse
            </b-button>
          </template>
          <template #cell(created_at)="data">
            {{ `${data.item.created_at}Z` | formatDate }}
          </template>
          <template #cell(remark_text)="data">
            <component
              :is="data.item.service == 'trips' ? 'b-link': 'div' "
              :to="{ path: `/trips/${data.item.external_reference_id}` }"
              :class="data.item.added_by == 'trips' ? 'linkStyle' : ''"
            >
              {{ data.item.remark_text }}
            </component>
          </template>
        </b-table>
        <base-pagination
          v-model="page"
          :total="totalTransactions"
          @new-per-page="perPage = $event"
        />
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BTable, BButton, BOverlay, BLink, BFormInput, BInputGroupAppend, BInputGroup, BRow, BCol,
} from 'bootstrap-vue'
import {
  computed, ref, watch,
} from '@vue/composition-api'
import vSelect from 'vue-select'
import formatDate from '@/utils/date-format'
import store from '@/store'
import router from '@/router'
import TransactionForm from './TransactionForm.vue'
import BasePagination from '@/UI/BasePagination.vue'
import ConfirmReverseTransactionModal from './ConfirmReverseTransactionModal.vue'

export default {
  components: {
    BOverlay,
    BCard,
    BCardTitle,
    BTable,
    BButton,
    BasePagination,
    TransactionForm,
    vSelect,
    BLink,
    BFormInput,
    BRow,
    BCol,
    BInputGroupAppend,
    BInputGroup,
    ConfirmReverseTransactionModal,
  },
  setup(_, ctx) {
    const displaySideBar = ref(false)
    const transactionType = ref('')
    const accountNameFilter = ref(null)
    const transactionId = ref(null)
    const selectTransaction = type => {
      transactionType.value = type
      displaySideBar.value = true
    }
    const tableColumns = [
      { key: 'created_at', label: 'Date & Time' },
      { key: 'transaction_type_label', label: 'Account Name' },
      { key: 'credit_amount', label: 'Credit' },
      { key: 'debit_amount', label: 'Debit' },
      { key: 'added_by', label: 'Added By' },
      { key: 'remark_text', label: 'Remark Text' },
      'actions',
    ]
    const totalTransactions = ref(0)
    const loadingTransactions = ref(false)
    const types = computed(() => store.getters['transactionTypes/getTransactionTypes'] || [])
    const transactions = computed(() => {
      const tableData = store.getters['driverView/getDriverTransactions']
      if (tableData) {
        totalTransactions.value = tableData.total
      }
      return tableData?.transactions
    })
    const downloadTransactionList = () => {
      let csv = 'Date & Time,Updated At,Account Name, Credit Amount,Debit Amount, Added By, Remark Text\n'
      const data = transactions.value.map(row => ({
        created_at: formatDate(row.created_at),
        updated_at: formatDate(row.updated_at),
        transaction_type_label: row.transaction_type_label,
        credit_amount: row.credit_amount,
        debit_amount: row.debit_amount,
        added_by: row.added_by,
        remark_text: row.remark_text,
      }))

      // Loop through the array
      data.forEach(row => {
        const rowValues = Object.entries(row)
        console.log('row vals', rowValues)
        const rowLength = rowValues.length
        // Loop through each object
        rowValues.forEach((key, index) => {
          // Last item reached, set a new line
          if (index + 1 === rowLength) {
            csv += `${key[1]}\n`
            return
          }
          // escaped the commas with the date key
          if (typeof key[1] === 'string' && key[1].includes(',')) {
            // eslint-disable-next-line no-param-reassign
            key[1] = key[1].replaceAll(',', '')
          }
          // assign a comma for each cell
          csv += `${key[1]},`
        })
      })
      const link = document.createElement('a')
      link.id = 'download-csv'
      link.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(csv)}`)
      link.setAttribute('download', `${router.currentRoute.params.id}.csv`)
      document.body.appendChild(link)
      document.querySelector('#download-csv').click()
    }

    const perPage = ref(10)
    const page = ref(1)
    const refetchPaginated = async () => {
      loadingTransactions.value = true
      await store.dispatch('driverView/fetchDriverTransactions', {
        page: page.value,
        size: perPage.value,
        transaction_type_id: accountNameFilter.value,
      })
      loadingTransactions.value = false
    }
    const reverseTransaction = id => {
      transactionId.value = id
      ctx.root.$bvModal.show('reversetransaction-modal')
    }
    const displayReverseBtn = transaction => {
      const now = new Date()
      const createdAt = new Date(transaction.created_at)
      const twoDays = 48 * 60 * 60 * 1000
      if (ctx.root.$can('read', 'transaction-actions') && transaction.transaction_type_label === 'Driver Fines' && !transaction.reversed && (now - createdAt) < twoDays) {
        return true
      }
      return false
    }
    watch([perPage, page], () => {
      refetchPaginated()
    })
    return {
      refetchPaginated,
      tableColumns,
      transactions,
      loadingTransactions,
      totalTransactions,
      accountNameFilter,
      perPage,
      page,
      displaySideBar,
      types,
      transactionType,
      selectTransaction,
      downloadTransactionList,
      reverseTransaction,
      transactionId,
      displayReverseBtn,
    }
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';
.linkStyle {
  color: blue;
}
</style>
